import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

interface EntityFeatureMappingProps {
  entityFeatureMapping: Record<string, string>;
  entityDefinitions: Record<string, any>;
  featureDefinitions: Record<string, any>;
  onChange: (entityFeatureMapping: Record<string, string>) => void;
}

const selectSx = (color: string) => ({
  bgcolor: color,
  color: '#1B0826',
  fontWeight: 500,
  fontSize: '13px',
  height: '26px',
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-select': { py: 0.5, px: 1 },
  borderRadius: '16px',
});

export const EntityFeatureMapping: React.FC<EntityFeatureMappingProps> = ({
  entityFeatureMapping,
  entityDefinitions,
  featureDefinitions,
  onChange,
}) => {
  const mappings = Object.entries(entityFeatureMapping || {});
  const entityNames = Object.keys(entityDefinitions);
  const featureNames = Object.keys(featureDefinitions);
  const mappedEntities = new Set(mappings.map(([e]) => e));

  const [adding, setAdding] = useState(false);
  const [newEntity, setNewEntity] = useState('');
  const [newFeature, setNewFeature] = useState('');

  const handleUpdateEntity = (oldEntity: string, newEntityKey: string) => {
    const updated = { ...entityFeatureMapping };
    const feature = updated[oldEntity];
    delete updated[oldEntity];
    updated[newEntityKey] = feature;
    onChange(updated);
  };

  const handleUpdateFeature = (entity: string, feature: string) => {
    onChange({ ...entityFeatureMapping, [entity]: feature });
  };

  const handleDelete = (entity: string) => {
    const updated = { ...entityFeatureMapping };
    delete updated[entity];
    onChange(updated);
  };

  const handleAdd = () => {
    if (!newEntity || !newFeature) return;
    onChange({ ...entityFeatureMapping, [newEntity]: newFeature });
    setAdding(false);
    setNewEntity('');
    setNewFeature('');
  };

  const availableEntities = (excludeEntity?: string) =>
    entityNames.filter((e) => !mappedEntities.has(e) || e === excludeEntity);

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Entity-to-Feature Mapping
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        When an entity is created for the first time, automatically enable a feature.
      </Typography>

      {/* Mapping rows */}
      {mappings.map(([entity, feature]) => (
        <Paper
          key={entity}
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={entity}
              onChange={(e) => handleUpdateEntity(entity, e.target.value)}
              sx={selectSx('#b8e6d5')}
            >
              {!entityNames.includes(entity) && (
                <MenuItem value={entity}>{entity}</MenuItem>
              )}
              {availableEntities(entity).map((name) => (
                <MenuItem key={name} value={name}>
                  {entityDefinitions[name]?.name || entityDefinitions[name]?.displayName || name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ArrowIcon sx={{ fontSize: 18, color: '#999', mx: 0.5 }} />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={feature}
              onChange={(e) => handleUpdateFeature(entity, e.target.value)}
              sx={selectSx('#b8e6d5')}
            >
              {!featureNames.includes(feature) && (
                <MenuItem value={feature}>{feature}</MenuItem>
              )}
              {featureNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {featureDefinitions[name]?.displayName || name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <IconButton size="small" color="error" onClick={() => handleDelete(entity)} sx={{ ml: 'auto' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Paper>
      ))}

      {/* Add row */}
      {adding ? (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1,
            border: '2px dashed #1976d2',
            borderRadius: 2,
            bgcolor: '#f0f7ff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={newEntity}
              onChange={(e) => setNewEntity(e.target.value)}
              displayEmpty
              sx={selectSx('#b8e6d5')}
            >
              <MenuItem value=""><em>entity</em></MenuItem>
              {availableEntities().map((name) => (
                <MenuItem key={name} value={name}>
                  {entityDefinitions[name]?.name || entityDefinitions[name]?.displayName || name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ArrowIcon sx={{ fontSize: 18, color: '#999', mx: 0.5 }} />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              displayEmpty
              sx={selectSx('#b8e6d5')}
            >
              <MenuItem value=""><em>feature</em></MenuItem>
              {featureNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {featureDefinitions[name]?.displayName || name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" gap={0.5} ml="auto">
            <Button
              size="small"
              onClick={() => { setAdding(false); setNewEntity(''); setNewFeature(''); }}
              sx={{ color: '#666', fontSize: '12px', textTransform: 'none', minWidth: 0 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleAdd}
              disabled={!newEntity || !newFeature}
              sx={{
                color: '#1B0826',
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'none',
                minWidth: 0,
                '&.Mui-disabled': { color: '#ccc' },
              }}
            >
              Add
            </Button>
          </Box>
        </Paper>
      ) : (
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => setAdding(true)}
          fullWidth
          disabled={availableEntities().length === 0}
          sx={{
            mt: 1,
            color: '#1B0826',
            borderColor: '#1B0826',
            '&:hover': {
              borderColor: '#1B0826',
              bgcolor: 'rgba(27, 8, 38, 0.04)',
            },
          }}
        >
          Add Mapping
        </Button>
      )}

      {mappings.length === 0 && !adding && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No mappings configured. Add one to auto-enable features when entities are first created.
        </Alert>
      )}
    </Box>
  );
};
